import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Grid } from '@mui/material';
import { Box, Button, FormControlLabel, Icons, MenuItem, SelectField, Stack, Switch, TextField, Typography, InputAdornment } from '@curves/ui-base';
import { Overview, ProductInfo } from '@curves/ui-shared';
import { parseDate } from '@Util/moment.util';
import { formatCurrencyFn, refIsRequiredError } from '@Util/utils';
import useOverview from '@Util/hook/useOverview';
import useOptions from '@Util/hook/useOptions';
import useClubInventoryApi from '@Apis/useClubInventoryApi';
import { CLUB_INVENTORY_SHOP_STOCK_DETAIL_ROUTER_URL } from '@Router/routerPath';

const DISPLAY_DATE_BY_SLASH = 'YYYY/MM/DD';

const toBoolean = (value) => value === true || value === 1 || value === '1' || value === 'true';

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const tableHeaderRow = [
    {
        cellKey: 'hasPendingDefective',
        className: 'px-0',
        headerLabel: '',
        formatCell(hasPendingDefective) {
            if (!hasPendingDefective) return null;
            return <Box className="stock-overview-defect-indicator" />;
        },
    },
    {
        cellKey: 'productName',
        headerLabel: '商品名稱',
        minWidth: 200,
        formatCell(productName, row) {
            return (
                <NavLink
                    to={CLUB_INVENTORY_SHOP_STOCK_DETAIL_ROUTER_URL(row?.productID || 0, row?.variantID || 0, row?.clubID || 0)}
                    className="stock-overview-product-link"
                >
                    <ProductInfo
                        showEditIcon={false}
                        onEdit={() => {}}
                        width={320}
                        product={{
                            sku: row?.sku,
                            name: row?.productName ?? productName,
                            spec: row?.variantName ?? '',
                        }}
                    />
                </NavLink>
            );
        },
    },
    { cellKey: 'categoryName', headerLabel: '分類' },
    { cellKey: 'clubName', headerLabel: '店鋪名稱' },
    {
        cellKey: 'defaultSalePrice',
        headerLabel: '預設販售價格',
        align: 'center',
        formatCell: (value) => formatCurrencyFn(value, { symbol: '$' }),
    },
    {
        cellKey: 'quantity',
        align: 'center',
        headerLabel: '可販售數量',
    },
    {
        cellKey: 'lastCountDate',
        align: 'center',
        headerLabel: '上一次盤點日',
        formatCell: (value) => parseDate(value, DISPLAY_DATE_BY_SLASH),
    },
    {
        cellKey: 'action',
        align: 'center',
        headerLabel: '操作',
        formatCell: (_, row) => (
            <Button
                component={NavLink}
                to={CLUB_INVENTORY_SHOP_STOCK_DETAIL_ROUTER_URL(row?.productID || 0, row?.variantID || 0, row?.clubID || 0)}
                variant="outlined"
                size="small"
                color="primary"
            >
                查看明細
            </Button>
        ),
    },
];

const DEFAULT_SEARCH_PARAMS = {
    clubID: 0,
    keyword: '',
    isDefective: false,
    saleCategory: '0',
    pageIndex: 1,
    pageSize: 20,
};

const ShopStockOverviewPage = () => {
    const { getClubInventoryOptionsApi, postClubInventoryOverviewApi } = useClubInventoryApi();

    const keywordRef = useRef(null);
    const categoryRef = useRef(null);
    const clubRef = useRef(null);
    const isDefectiveRef = useRef(null);

    const { options } = useOptions({
        getOptionsApi: getClubInventoryOptionsApi,
        isFetchOnMount: true,
    });

    const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi: postClubInventoryOverviewApi,
        defaultSearchParams: DEFAULT_SEARCH_PARAMS,
        isFetchOnMount: false,
        enableQueryString: true,
    });

    const normalizedOptions = /** @type {any} */ (options || {});
    const clubOptions = normalizedOptions.clubOptions || [];
    const firstClubID = useMemo(() => Number(clubOptions?.[0]?.value || 0), [clubOptions]);

    const categoryMenuSource = useMemo(() => {
        const raw = ensureArray(normalizedOptions.categoryOptions);
        return raw.length > 0 ? raw : [{ text: '全部', value: '0' }];
    }, [normalizedOptions.categoryOptions]);

    const getParamsData = useCallback(() => {
        const rawClub = clubRef.current?.getResult?.();
        const clubID = Number(rawClub != null && rawClub !== '' ? rawClub : searchParams?.clubID) || 0;
        return {
            ...searchParams,
            clubID,
            keyword: keywordRef.current?.getResult?.() ?? '',
            isDefective: Boolean(isDefectiveRef.current?.checked),
            saleCategory: categoryRef.current?.getResult?.() ?? '0',
            pageIndex: 1,
        };
    }, [searchParams]);

    const handleSearchSubmit = useCallback(() => {
        const isError = refIsRequiredError(clubRef, keywordRef, categoryRef);
        if (isError) return false;
        return getParamsData();
    }, [getParamsData]);

    /** 避免相同查詢條件重複打 API（含網址尚未寫入 clubID 與已寫入時其實同一筆請求） */
    const lastOverviewFetchSigRef = useRef(null);
    const searchClubID = Number(searchParams?.clubID || 0) || 0;
    const searchKeyword = String(searchParams?.keyword ?? '');
    const searchIsDefective = toBoolean(searchParams?.isDefective);
    const searchSaleCategory = String(searchParams?.saleCategory ?? '0');
    const searchPageIndex = Number(searchParams?.pageIndex) || 1;
    const searchPageSize = Number(searchParams?.pageSize) || 20;

    useEffect(() => {
        // 等店舖選項載入後才查詢；clubID 允許為 0（全部店鋪），故不可用 firstClubID 當守門條件
        if (clubOptions.length === 0) return;
        const targetClubID = searchClubID || firstClubID;
        const payload = {
            ...DEFAULT_SEARCH_PARAMS,
            clubID: targetClubID,
            keyword: searchKeyword,
            isDefective: searchIsDefective,
            saleCategory: searchSaleCategory,
            pageIndex: searchPageIndex,
            pageSize: searchPageSize,
        };
        const sig = JSON.stringify({
            clubID: payload.clubID,
            keyword: payload.keyword,
            isDefective: payload.isDefective,
            saleCategory: payload.saleCategory,
            pageIndex: payload.pageIndex,
            pageSize: payload.pageSize,
        });
        if (lastOverviewFetchSigRef.current === sig) return;
        lastOverviewFetchSigRef.current = sig;
        getOverviewData(payload);
    }, [clubOptions.length, firstClubID, searchClubID, searchKeyword, searchIsDefective, searchSaleCategory, searchPageIndex, searchPageSize, getOverviewData]);

    const contextValue = useMemo(
        () => ({
            overviewData,
            searchParams,
            getOverviewData,
            updateOverviewPage,
        }),
        [overviewData, searchParams, getOverviewData, updateOverviewPage],
    );

    return (
        <Box className="px-3 py-2">
            <Stack justifyContent="space-between" alignItems="center" className="mb-2">
                <Typography variant="h5" className="mb-0 font-weight-bold">
                    店鋪庫存總覽
                </Typography>
            </Stack>

            <Overview contextValue={contextValue} idKey="stockID">
                <Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="搜尋商品"
                                ref={keywordRef}
                                key={searchParams.keyword ?? ''}
                                defaultValue={searchParams.keyword}
                                inputProps={{ placeholder: '請輸入商品名稱或商品SKU' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icons.Magnifier />
                                        </InputAdornment>
                                    ),
                                }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <SelectField
                                ref={clubRef}
                                key={`${String(searchParams.clubID ?? firstClubID ?? '')}-${clubOptions.length}`}
                                label="店鋪"
                                defaultValue={searchParams.clubID ? String(searchParams.clubID) : firstClubID ? String(firstClubID) : ''}
                                required
                                fullWidth
                            >
                                {clubOptions.map(({ text, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {text}
                                    </MenuItem>
                                ))}
                            </SelectField>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="end" alignItems="center">
                                <FormControlLabel
                                    key={String(searchParams.isDefective)}
                                    control={<Switch defaultChecked={toBoolean(searchParams.isDefective)} inputProps={{ ref: isDefectiveRef }} />}
                                    prefixLabel="只顯示瑕疵商品"
                                />
                                <Button variant="contained" color="primary" type="submit">
                                    搜尋
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Overview.Search>

                <Stack direction="row" justifyContent="space-between" alignItems="center" className="mb-2">
                    <SelectField
                        ref={categoryRef}
                        key={`${String(searchParams.saleCategory)}-${categoryMenuSource.length}`}
                        defaultValue={searchParams.saleCategory}
                        onChange={(e) =>
                            getOverviewData({
                                ...searchParams,
                                saleCategory: e.target.value,
                                pageIndex: 1,
                            })
                        }
                        required
                    >
                        {categoryMenuSource.map(({ text, value }) => (
                            <MenuItem key={value} value={value}>
                                {text}
                            </MenuItem>
                        ))}
                    </SelectField>
                    <Overview.TotalCount className="mb-0" showCurrentCount />
                </Stack>

                <Overview.Table className="mb-3" headerRow={tableHeaderRow} />
                <Overview.Pagination />
            </Overview>
        </Box>
    );
};

export default ShopStockOverviewPage;
